import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import { SEND_EMAIL_MUTATION } from "../../graphql/mutations";
import SmsConsentCheckbox, { SMS_CONSENT_TEXT } from "../../components/legal/SmsConsentCheckbox";

/**
 * ContactUsForm (care flavored)
 * - Expanded "reason" choices to reflect care provider needs.
 * - Optional facility/business and license fields included in the email if provided.
 * - Routes to different inboxes based on reason (fallbacks to support).
 *
 * Props:
 * - onClose (required): callback when form is closed elsewhere (not used here but preserved)
 */

const REASONS = [
  { value: "issue", label: "Site / Technical Issue", to: "support@plany365.com" },
  { value: "question", label: "General Question", to: "support@plany365.com" },
  { value: "licensing", label: "Care Licensing / Compliance Question", to: "support@plany365.com" },
  { value: "placement", label: "Resident Placement / Availability", to: "support@plany365.com" },
  { value: "listing", label: "Update My Listing / Beds", to: "support@plany365.com" },
  { value: "billing", label: "Billing / Subscription", to: "support@plany365.com" },
  { value: "training", label: "Training / Onboarding", to: "support@plany365.com" },
  { value: "feature", label: "Feature Request", to: "support@plany365.com" },
  { value: "data_correction", label: "Data Correction (Address/Phone/Hours)", to: "support@plany365.com" },
  { value: "partnership", label: "Partnership / Sales", to: "sales@plany365.com" },
  { value: "demo", label: "Request a Demo", to: "support@plany365.com" },
];

const getRecipientForReason = (reason) =>
  REASONS.find((r) => r.value === reason)?.to || "support@plany365.com";

const ContactUsForm = ({
  onClose,
  initialName = "",
  initialFacility = "",
  initialLicense = "",
  initialEmail = "",
  initialPhone = "",
  initialReason = "question",
  initialSubject = "",
  initialMessage = "",
}) => {
  const [sendEmail] = useMutation(SEND_EMAIL_MUTATION);

  // Form state
  const [name, setName] = useState(initialName);
  const [facility, setFacility] = useState(initialFacility);      // optional care facility/business
  const [license, setLicense] = useState(initialLicense);        // optional business license number
  const [email, setEmail] = useState(initialEmail);
  const [phone, setPhone] = useState(initialPhone);            // optional contact number
  const [reason, setReason] = useState(initialReason);
  const [subject, setSubject] = useState(initialSubject);
  const [message, setMessage] = useState(initialMessage);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [smsConsent, setSmsConsent] = useState(false);

  const validate = () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = "Name is required";
    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!subject.trim()) newErrors.subject = "Subject is required";
    if (!message.trim()) newErrors.message = "Message is required";
    if (!smsConsent) newErrors.smsConsent = "SMS consent is required before submitting this form.";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validate();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    const reasonMeta = REASONS.find((r) => r.value === reason);
    const to = getRecipientForReason(reason);
    const reasonLabel = reasonMeta ? reasonMeta.label : "General";

    // Build bodies
    const textBody =
      `Hello,

You have received a new enquiry from ${name}.
Reason: ${reasonLabel}
Subject: ${subject}
${facility ? `Facility/Business: ${facility}\n` : ""}${license ? `License #: ${license}\n` : ""}${phone ? `Phone: ${phone}\n` : ""}Email: ${email}
SMS consent: ${smsConsent ? "Yes" : "No"}
SMS consent text: ${SMS_CONSENT_TEXT}

Message:
${message}

Best regards,
${name}`;

    const htmlBody = `
      <p>Hello,</p>
      <p>You have received a new enquiry from <strong>${escapeHtml(name)}</strong>.</p>
      <p><strong>Reason:</strong> ${escapeHtml(reasonLabel)}</p>
      <p><strong>Subject:</strong> ${escapeHtml(subject)}</p>
      ${facility
        ? `<p><strong>Facility/Business:</strong> ${escapeHtml(facility)}</p>`
        : ""
      }
      ${license ? `<p><strong>License #:</strong> ${escapeHtml(license)}</p>` : ""}
      ${phone ? `<p><strong>Phone:</strong> ${escapeHtml(phone)}</p>` : ""}
      <p><strong>Email:</strong> ${escapeHtml(email)}</p>
      <p><strong>SMS consent:</strong> ${smsConsent ? "Yes" : "No"}</p>
      <p><strong>SMS consent text:</strong> ${escapeHtml(SMS_CONSENT_TEXT)}</p>
      <p><strong>Message:</strong><br/>${escapeHtml(message).replace(/\n/g, "<br/>")}</p>
      <p>Best regards,<br/>${escapeHtml(name)}</p>
    `;

    try {
      await sendEmail({
        variables: {
          from_email: null,
          to,
          subject: `[Care] ${reasonLabel} - ${subject}`,
          text: textBody,
          html: htmlBody,
        },
      });
      const userSubject = `We received your request - ${subject}`;
      const userTextBody =
        `Hello ${name},

Thank you for contacting Plany365 Care.
We received your request and our team will follow up shortly.

Reason: ${reasonLabel}
Subject: ${subject}

Your message:
${message}

If this is urgent, call Plany Assist at (833) 368-1399.
`;
      const userHtmlBody = `
        <p>Hello ${escapeHtml(name)},</p>
        <p>Thank you for contacting <strong>Plany365 Care</strong>.</p>
        <p>We received your request and our team will follow up shortly.</p>
        <p><strong>Reason:</strong> ${escapeHtml(reasonLabel)}</p>
        <p><strong>Subject:</strong> ${escapeHtml(subject)}</p>
        <p><strong>Your message:</strong><br/>${escapeHtml(message).replace(/\n/g, "<br/>")}</p>
        <p>If this is urgent, call Plany Assist at <strong>(833) 368-1399</strong>.</p>
      `;

      await sendEmail({
        variables: {
          from_email: null,
          to: email,
          subject: userSubject,
          text: userTextBody,
          html: userHtmlBody,
        },
      });

      setSubmitted(true);
      setErrors({});
    } catch (error) {
      console.error("Error sending email:", error);
      alert("Sorry, we couldn't send your message. Please try again.");
    }
  };

  if (submitted) {
    return (
      <div style={{ textAlign: "center", padding: "1rem" }}>
        <p style={{ color: "green" }}>
          Thank you! We received your request and will get back to you shortly.
        </p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 640, margin: "0 auto" }}>
      <h2>Contact Us (Care)</h2>

      <form onSubmit={handleSubmit} noValidate>
        {/* Name */}
        <FormRow label="Your Name:" htmlFor="name" error={errors.name}>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={inputStyle}
            aria-invalid={!!errors.name}
          />
        </FormRow>

        {/* Facility/Business (optional) */}
        <FormRow label="Facility / Business (optional):" htmlFor="facility">
          <input
            id="facility"
            type="text"
            value={facility}
            onChange={(e) => setFacility(e.target.value)}
            style={inputStyle}
          />
        </FormRow>

        {/* License # (optional) */}
        <FormRow label="License # (optional):" htmlFor="license">
          <input
            id="license"
            type="text"
            value={license}
            onChange={(e) => setLicense(e.target.value)}
            style={inputStyle}
          />
        </FormRow>

        {/* Email */}
        <FormRow label="Email:" htmlFor="email" error={errors.email}>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
            aria-invalid={!!errors.email}
          />
        </FormRow>

        {/* Phone (optional) */}
        <FormRow label="Phone (optional):" htmlFor="phone">
          <input
            id="phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            style={inputStyle}
          />
        </FormRow>

        <SmsConsentCheckbox
          checked={smsConsent}
          onChange={setSmsConsent}
          error={errors.smsConsent}
          sx={{ marginBottom: "14px" }}
        />

        {/* Reason */}
        <FormRow label="Reason for Contact:" htmlFor="reason">
          <select
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={inputStyle}
          >
            {REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </FormRow>

        {/* Subject */}
        <FormRow label="Subject:" htmlFor="subject" error={errors.subject}>
          <input
            id="subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            style={inputStyle}
            aria-invalid={!!errors.subject}
          />
        </FormRow>

        {/* Message */}
        <FormRow label="Message:" htmlFor="message" error={errors.message}>
          <textarea
            id="message"
            rows={6}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            style={{ ...inputStyle, resize: "vertical" }}
            aria-invalid={!!errors.message}
          />
        </FormRow>

        <button type="submit" style={buttonStyle}>Submit</button>
      </form>
    </div>
  );
};

/* ---------- tiny helpers ---------- */
const inputStyle = { width: "100%", padding: "8px" };
const buttonStyle = { padding: "10px 20px", cursor: "pointer" };

const FormRow = ({ label, htmlFor, error, children }) => (
  <div style={{ marginBottom: "14px" }}>
    <label htmlFor={htmlFor}>{label}</label>
    <br />
    {children}
    {error ? <p style={{ color: "red", marginTop: 6 }}>{error}</p> : null}
  </div>
);

// Basic HTML escaping to avoid broken markup in email
function escapeHtml(s) {
  return String(s)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

export default ContactUsForm;

